import type { MeResponse } from "@safeplate/contracts";
import { Button, Card, CardContent } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, LockKeyhole } from "lucide-react";
import { useEffect, type ReactNode } from "react";

import { useNavigate } from "@tanstack/react-router";

import { signOut } from "../api/auth.js";
import { displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { ErrorState, LoadingState } from "../components/states.js";
import { organisationChoices } from "../state/selection.js";
import { addPerson } from "./people-store.js";
import {
  bindSharedDevice,
  enrollCurrentDeviceAccount,
  getSharedDeviceRoster,
  isUnboundRoster,
  scopeMeToOrganisation,
  unbindSharedDevice,
} from "./quick-access-api.js";

export function SharedDeviceGate({
  children,
  me,
}: {
  children: (scopedMe: MeResponse) => ReactNode;
  me: MeResponse;
}) {
  const queryClient = useQueryClient();
  const organisations = organisationChoices(me);
  const roster = useQuery({
    queryFn: getSharedDeviceRoster,
    queryKey: ["shared-device-roster"],
    retry: false,
  });
  const bind = useMutation({
    mutationFn: bindSharedDevice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shared-device-roster"],
      });
    },
  });
  const enroll = useMutation({
    mutationFn: enrollCurrentDeviceAccount,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shared-device-roster"],
      });
    },
  });
  const remove = useMutation({
    mutationFn: unbindSharedDevice,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["shared-device-roster"],
      });
    },
  });
  const navigate = useNavigate();
  // Leaves only this account; the computer stays with its business.
  const leave = useMutation({
    mutationFn: () => signOut().catch(() => undefined),
    onSettled: async () => {
      queryClient.clear();
      await navigate({ to: "/sign-in" });
    },
  });
  const missingBinding = isUnboundRoster(roster.data, roster.error);

  const belongsHere = Boolean(
    roster.data &&
    organisations.some(
      (organisation) => organisation.id === roster.data?.org_id,
    ),
  );

  useEffect(() => {
    if (
      belongsHere &&
      !enroll.isPending &&
      !enroll.isSuccess &&
      !enroll.error
    ) {
      enroll.mutate();
    }
  }, [
    belongsHere,
    enroll.error,
    enroll.isPending,
    enroll.isSuccess,
    enroll.mutate,
  ]);

  const deviceId = roster.data?.device_id;
  const orgId = roster.data?.org_id;
  useEffect(() => {
    if (!belongsHere || !enroll.isSuccess || !deviceId || !orgId) return;
    const membership = organisationChoices(me).find(
      (organisation) => organisation.id === orgId,
    );
    if (!membership) return;
    addPerson(deviceId, {
      addedAt: new Date().toISOString(),
      email: me.user.email,
      name: me.user.name,
      role: membership.role,
      staffProfileId:
        me.staff_profiles.find((profile) => profile.org_id === orgId)
          ?.staff_profile_id ?? null,
      userId: me.user.id,
    });
  }, [belongsHere, deviceId, enroll.isSuccess, me, orgId]);

  useEffect(() => {
    const only = organisations[0];
    if (
      missingBinding &&
      organisations.length === 1 &&
      only &&
      !bind.isPending &&
      !bind.isSuccess &&
      !bind.error
    ) {
      bind.mutate(only.id);
    }
  }, [
    bind.error,
    bind.isPending,
    bind.isSuccess,
    bind.mutate,
    missingBinding,
    organisations,
  ]);

  if (roster.isPending || (missingBinding && bind.isPending)) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <LoadingState label="Opening SafePlate" waking />
      </main>
    );
  }

  if (roster.data) {
    if (belongsHere) {
      if (enroll.isPending || !enroll.isSuccess) {
        if (enroll.error) {
          return (
            <main className="grid min-h-dvh place-items-center bg-canvas p-5">
              <ErrorState
                message={displayError(enroll.error)}
                onAction={() => enroll.mutate()}
                title="Couldn’t add you to this computer"
              />
            </main>
          );
        }
        return (
          <main className="grid min-h-dvh place-items-center bg-canvas p-5">
            <LoadingState label="Opening SafePlate" />
          </main>
        );
      }
      return children(scopeMeToOrganisation(me, roster.data.org_id));
    }
    return (
      <GateFrame>
        <LockKeyhole className="size-7 text-emerald-800" aria-hidden="true" />
        <h1 className="mt-5 text-2xl font-black">
          This computer is set up for {roster.data.org_name}
        </h1>
        {remove.error ? (
          <p className="mt-4 text-sm text-red-800">
            {displayError(remove.error)}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap gap-3">
          <Button disabled={remove.isPending} onClick={() => remove.mutate()}>
            Sign everyone out of this computer
          </Button>
          <Button
            disabled={leave.isPending}
            onClick={() => leave.mutate()}
            variant="outline"
          >
            Sign out
          </Button>
        </div>
      </GateFrame>
    );
  }

  if (!missingBinding) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <ErrorState
          message={displayError(roster.error)}
          onAction={() => void roster.refetch()}
          title="Couldn’t open SafePlate"
        />
      </main>
    );
  }

  if (organisations.length === 1) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        {bind.error ? (
          <ErrorState
            message={displayError(bind.error)}
            onAction={() => bind.mutate(organisations[0]!.id)}
            title="Couldn’t open SafePlate"
          />
        ) : (
          <LoadingState label="Opening SafePlate" waking />
        )}
      </main>
    );
  }

  return (
    <GateFrame>
      <Building2 className="size-7 text-emerald-800" aria-hidden="true" />
      <h1 className="mt-5 text-2xl font-black">Choose a business</h1>
      <div className="mt-6 space-y-2">
        {organisations.map((organisation) => (
          <Button
            className="w-full justify-start"
            disabled={bind.isPending}
            key={organisation.id}
            onClick={() => bind.mutate(organisation.id)}
            variant="outline"
          >
            <Building2 className="size-4" aria-hidden="true" />
            {organisation.name}
          </Button>
        ))}
      </div>
      {bind.error ? (
        <p className="mt-4 text-sm text-red-800">{displayError(bind.error)}</p>
      ) : null}
    </GateFrame>
  );
}

function GateFrame({ children }: { children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5">
      <div className="w-full max-w-lg">
        <div className="mb-8">
          <Brand />
        </div>
        <Card>
          <CardContent className="p-7">{children}</CardContent>
        </Card>
      </div>
    </main>
  );
}
